/**
 * useIcarusChat — der globale Dädalus-Assistent (Layout-FAB, Strg/Cmd+J).
 *
 * Was hier vorher stand und warum es weg ist
 * ──────────────────────────────────────────
 * Der Hook sprach mit einem eigenen `fetch` gegen `icarus-chat` und schickte
 * `{ messages, context, stream: true }`. Die Function las nur `{ message,
 * history }` und kennt kein SSE — jede Nachricht kam als
 * `400 { error: "Nachricht fehlt" }` zurück und landete als
 * „Assistenten-Fehler …" im Panel. Der Assistent hat, seit er eingeführt
 * wurde, nie eine Antwort geliefert.
 *
 * Jetzt geht der Hook durch `callIcarusChat` — denselben Weg wie der
 * Cockpit-Chat. Damit bekommt der globale Assistent obendrein alles, was
 * er vorher nicht hatte:
 *
 *   - den kompletten Tool-Loop (Patient anlegen, Termin buchen,
 *     GNCP-Entwurf, `navigate_to` …), inklusive Consent-Gate in `tools.ts`
 *   - echten Gesprächsverlauf statt eines Closures, das nur die
 *     Begrüßung sah
 *   - Persistenz in `icarus_conversations` (§4 „Chat-Persistenz" in
 *     HANDOVER.md)
 *   - den Patientenkontext, den das Panel schon anzeigte, aber nie
 *     übermittelte
 *
 * `useChatStream` bleibt als State-Primitive erhalten (optimistisches
 * Anhängen, Pending-Bubble, Abbruch). Da `functions.invoke` keinen
 * AbortSignal kennt, kappt „Stopp" die Anzeige sofort; die bereits
 * laufende Function-Ausführung läuft serverseitig zu Ende — Tool-Calls
 * werden also nicht halb ausgeführt.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { resolveAssistantName } from '@/config/assistant';
import { conversationsRepo } from '@/data';
import type { ChatMessage } from '@/data/conversations';
import { useChatStream, type ChatStreamRequest } from '@/components/chat';
import { initialGreeting } from '@/components/dashboard/nutri-chat/utils';
import { callIcarusChat } from '@/services/icarusChat';
import { useConversationPersistence } from '@/lib/ai/useConversationPersistence';
import type { TurnMessage } from '@/lib/ai/chatRequestContract';

/** ID der Begrüßungs-Bubble — nie Teil von Verlauf oder Persistenz. */
export const WELCOME_MESSAGE_ID = 'welcome';

export type IcarusChatContext = {
  /** Current route, e.g. "/patients/uuid" */
  currentPath?: string;
  /** Patient ID if the user is viewing a specific patient */
  patientId?: string;
};

/**
 * Verwirft Begrüßung, Pending-Platzhalter und fehlgeschlagene Runden.
 * Was übrig bleibt, geht sowohl an das Modell als auch in die Persistenz.
 */
function toTurns(
  messages: ReadonlyArray<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    pending?: boolean;
    error?: string;
  }>,
): TurnMessage[] {
  return messages
    .filter((m) => m.id !== WELCOME_MESSAGE_ID && !m.pending && !m.error && m.content.trim())
    .map((m) => ({ role: m.role, content: m.content }));
}

export function useIcarusChat(ctx: IcarusChatContext = {}) {
  const { profile } = useAuth();
  const orgId = profile?.organization_id ?? null;
  const userId = profile?.user_id ?? null;
  const assistantName = resolveAssistantName(profile?.assistant_name);

  const chat = useChatStream({
    initialMessages: [
      {
        id: WELCOME_MESSAGE_ID,
        role: 'assistant',
        content: initialGreeting(assistantName),
      },
    ],
  });

  const persistence = useConversationPersistence({ orgId, userId });
  const { persist, resume, reset: resetPersistence } = persistence;

  // `request` läuft in einem Callback, dessen Closure sonst auf dem Stand
  // des Erstellungs-Renders einfriert. Der Ref hält den aktuellen Verlauf.
  const messagesRef = useRef(chat.messages);
  useEffect(() => {
    messagesRef.current = chat.messages;
  }, [chat.messages]);

  // ── Auto-Restore ────────────────────────────────────────────────────
  // Beim ersten Öffnen den eigenen letzten Faden zurückholen. Bewusst
  // user-gescopt (nicht org-weit wie die Cockpit-Sidebar) — sonst landet
  // man im Gespräch einer Kollegin.
  const { data: lastConversation } = useQuery({
    queryKey: ['icarus-assistant-last-conversation', orgId, userId],
    enabled: !!orgId && !!userId,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const rows = await conversationsRepo.listUserConversations(orgId!, userId!, 1);
      return rows[0] ?? null;
    },
  });

  const restoredRef = useRef(false);
  const { setMessages } = chat;
  useEffect(() => {
    if (restoredRef.current) return;
    if (!lastConversation || lastConversation.messages.length === 0) return;
    // Nur solange die Sitzung unberührt ist — ein bereits getipptes
    // Gespräch darf nicht rückwirkend überschrieben werden.
    if (messagesRef.current.some((m) => m.id !== WELCOME_MESSAGE_ID)) return;

    restoredRef.current = true;
    resume(lastConversation.id);
    setMessages((prev) => [
      ...prev,
      ...lastConversation.messages.map((m, i) => ({
        id: `restored-${lastConversation.id}-${i}`,
        role: m.role,
        content: m.content,
      })),
    ]);
  }, [lastConversation, resume, setMessages]);

  const request: ChatStreamRequest = useCallback(
    async ({ message }) => {
      const history = toTurns(messagesRef.current);

      const data = await callIcarusChat({
        message,
        history,
        context: {
          patientId: ctx.patientId ?? null,
          currentPath: ctx.currentPath ?? null,
        },
        assistantName,
      });

      if (data.error) throw new Error(data.error);

      const reply =
        (typeof data.response === 'string' && data.response) ||
        (typeof data.message === 'string' && data.message) ||
        'Keine Antwort erhalten.';

      const stored: ChatMessage[] = [
        ...history,
        { role: 'user', content: message },
        { role: 'assistant', content: reply },
      ];
      void persist(stored);

      return reply;
    },
    [ctx.patientId, ctx.currentPath, assistantName, persist],
  );

  const send = useCallback(
    (text: string) => chat.send(text, request),
    [chat, request],
  );

  /** Frisches Gespräch: Verlauf leeren, nächste Runde legt eine neue Zeile an. */
  const newConversation = useCallback(() => {
    restoredRef.current = true;
    resetPersistence();
    setMessages([
      {
        id: WELCOME_MESSAGE_ID,
        role: 'assistant',
        content: initialGreeting(assistantName),
      },
    ]);
  }, [assistantName, resetPersistence, setMessages]);

  return {
    ...chat,
    send,
    newConversation,
    assistantName,
    conversationId: persistence.conversationId,
  };
}
